import { Box, Grid, Typography, List, ListItem, Avatar } from "@mui/material";
import moment from "moment";
import RectangleIcon from "@mui/icons-material/Rectangle";
import React from "react";
import { useNavigate } from "react-router-dom";

interface ConsumerGroupTableProps {
    columnFilters: any;
    consumerGroups: any[];
    type: "active" | "pending" | "rejected";
}

const ConsumerGroupTable: React.FC<ConsumerGroupTableProps> = ({ type, consumerGroups, columnFilters }) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    const statusColors = {
        active: "#12B76A",
        pending: "#FDB022",
        rejected: "#F04438",
    };

    const statusBgColors = {
        active: "#FFFFFF",
        pending: "#FDB02210",
        rejected: "#F0443810",
    };

    return (
        <List sx={{ bgcolor: "#fafafa" }}>
            {/* ## 1. ADDED headers for new columns and ADJUSTED grid sizing */}
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px" }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        <Grid item xs={2.5}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Bold">CONSUMER GROUP NAME</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Bold">OWNER</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Bold">RATE LIMIT</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Bold">BURST LIMIT</Typography>
                        </Grid>
                        <Grid item xs={1.5}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Bold">QUOTA PERIOD</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Bold">CREATED ON</Typography>
                        </Grid>
                    </Grid>
                </Box>
            </ListItem>
            {consumerGroups.map((cg: any, index: number) => (
                <ListItem
                    key={cg.id}
                    sx={{
                        border: cardHoverIndex === index ? "1px solid #000" : "1px solid #00000000",
                        mt: 1,
                        bgcolor: statusBgColors[type],
                        borderRadius: "8px",
                        cursor: "pointer",
                    }}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => setCardHoverIndex(null)}
                    onClick={() => navigate(`/consumer-group/details/${cg.id}`)}
                >
                    <Box width="100%">
                        {/* ## 2. ADDED cells for new data and ADJUSTED grid sizing */}
                        <Grid container direction="row" alignItems="center" justifyContent="space-between">
                            <Grid item xs={2.5}>
                                {columnFilters.name && (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <RectangleIcon sx={{ color: statusColors[type], bgcolor: statusColors[type], width: "4px", height: "24px", borderRadius: '2px', marginRight: "8px" }} />
                                        <Typography fontSize="14px" fontFamily="Gilroy-Medium" noWrap>
                                            {cg.name}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={2}>
                                {columnFilters.owner && (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Avatar 
                                            src={cg.ownerAvatar} // <-- USE the 'src' prop here
                                            sx={{ marginRight: "8px", height: 30, width: 30, bgcolor: 'grey.300', fontSize: '14px' }}
                                        >
                                            {/* This is now a fallback for when there's no image */}
                                            {cg.owner?.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" noWrap>
                                            {cg.owner || '-'}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={2}>
                                {columnFilters.rate && (
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                        {cg.rate ? `${cg.rate} req/s` : '-'}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={2}>
                                {columnFilters.burst && (
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                        {cg.burst ? `${cg.burst} req/s` : '-'}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={1.5}>
                                {columnFilters.quotaPeriod && (
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" sx={{ textTransform: 'capitalize' }}>
                                        {cg.quotaPeriod || '-'}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={2}>
                                {columnFilters.createdAt && (
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                            {moment(cg.createdAt).format("DD/MM/YYYY | hh:mm A")}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default ConsumerGroupTable;